/* eslint-disable max-len */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect } from 'react';
import GlobalContext from '../../../../components/Context/GloablContext';
import ToggleSwitch from '../../../../components/ToggleSwitch';
import { 
    useUpdateObserverAlertPreferencesMutation,
    useViewUserQuery
} from '../../../../features/patientSlice';

export default function ObserverAlertPreferencesSection() {
    const { setToastmessage, setToastErrorMessage } = useContext(GlobalContext);
    // Alert preferences come from view-profile endpoint in userDetails
    const { data: profileData } = useViewUserQuery({ refetchOnMountOrArgChange: true });
    const [updateObserverAlertPreferences] = useUpdateObserverAlertPreferencesMutation();
    const [updatingField, setUpdatingField] = useState(null);
    const [optimisticUpdates, setOptimisticUpdates] = useState({});

    // Get observer's alert preferences and mobile number from profile
    const userDetails = profileData?.userDetails || {};
    const observerMobileNumber = userDetails?.mobile_number || '';
    const hasValidMobileNumber = observerMobileNumber && observerMobileNumber !== '';

    const handleObserverToggle = async (field, value) => {
        // Validate SMS toggles require mobile number
        if ((field === 'sms_alert' || field === 'reading_received_alert') && value === true && !hasValidMobileNumber) {
            setToastErrorMessage('Mobile number is required to enable SMS alerts');
            return;
        }

        const fieldKey = `observer-${field}`;
        setUpdatingField(fieldKey);
        
        // Optimistic update: immediately update local state
        setOptimisticUpdates(prev => ({
            ...prev,
            [fieldKey]: value
        }));

        try {
            const response = await updateObserverAlertPreferences({ [field]: value });
            if (response?.error?.data?.message) {
                // Rollback optimistic update on error
                setOptimisticUpdates(prev => {
                    const newState = { ...prev };
                    delete newState[fieldKey];
                    return newState;
                });
                setToastErrorMessage(response.error.data.message);
            } else {
                setToastmessage('Alert preference updated successfully');
                // Don't clear optimistic update here - let useEffect handle it when cache updates
            }
        } catch (error) {
            // Rollback optimistic update on error
            setOptimisticUpdates(prev => {
                const newState = { ...prev };
                delete newState[fieldKey];
                return newState;
            });
            setToastErrorMessage('Failed to update alert preference');
        } finally {
            setUpdatingField(null);
        }
    };

    // Alert preferences are in userDetails object from view-profile API
    const observerData = userDetails || {};

    // Clear optimistic updates when server data matches the optimistic value
    useEffect(() => {
        if (observerData && Object.keys(observerData).length > 0 && Object.keys(optimisticUpdates).length > 0) {
            setOptimisticUpdates(prev => {
                const newState = { ...prev };
                let hasChanges = false;
                Object.keys(newState).forEach(key => {
                    if (key.startsWith('observer-')) {
                        const field = key.replace('observer-', '');
                        const serverValue = observerData[field] === true || observerData[field] === "true" || observerData[field] === 1;
                        const optimisticValue = newState[key];
                        // If server data matches optimistic value, clear the optimistic update
                        if (serverValue === optimisticValue) {
                            delete newState[key];
                            hasChanges = true;
                        }
                    }
                });
                return hasChanges ? newState : prev;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [observerData]);

    // Helper function to get toggle value (server data or optimistic update)
    const getToggleValue = (field) => {
        const optimisticKey = `observer-${field}`;
        if (optimisticUpdates.hasOwnProperty(optimisticKey)) {
            return optimisticUpdates[optimisticKey];
        }
        return observerData[field] === true || observerData[field] === "true" || observerData[field] === 1;
    };

    return (
        <>
            {/* Observer Alert Settings Section */}
            <div className='flex items-center my-3.5'>
                <h1 className='text-lg font-normal text-[#000000] min-w-max'>Enable Active Alerts</h1>
                <div className='relative w-full h-[1px] bg-[#008586] ml-3 down-arraow'></div>
            </div>
            <div className="flex -mx-1 flex-wrap mb-6">
                <div className="w-1/4 px-1 mb-5">
                    <div className="w-full">
                        <p className="text-sm font-normal text-[#000] mb-2">Alerts by SMS</p>
                        <ToggleSwitch
                            id="observer-sms-alert"
                            checked={getToggleValue('sms_alert')}
                            onChange={(value) => handleObserverToggle('sms_alert', value)}
                            disabled={updatingField === 'observer-sms_alert'}
                            dataTestId="observer-sms-alert-toggle"
                        />
                        {!hasValidMobileNumber && (
                            <p className="text-xs text-[#FF0000] mt-1">Mobile number required</p>
                        )}
                    </div>
                </div>
                <div className="w-1/4 px-1 mb-5">
                    <div className="w-full">
                        <p className="text-sm font-normal text-[#000] mb-2">Alerts by Email</p>
                        <ToggleSwitch
                            id="observer-email-alert"
                            checked={getToggleValue('email_alert')}
                            onChange={(value) => handleObserverToggle('email_alert', value)}
                            disabled={updatingField === 'observer-email_alert'}
                            dataTestId="observer-email-alert-toggle"
                        />
                    </div>
                </div>
                <div className="w-1/4 px-1 mb-5">
                    <div className="w-full">
                        <p className="text-sm font-normal text-[#000] mb-2">Include Missed Readings</p>
                        <ToggleSwitch
                            id="observer-missed-reading-alert"
                            checked={getToggleValue('missed_reading_alert')}
                            onChange={(value) => handleObserverToggle('missed_reading_alert', value)}
                            disabled={updatingField === 'observer-missed_reading_alert'}
                            dataTestId="observer-missed-reading-alert-toggle"
                        />
                    </div>
                </div>
                <div className="w-1/4 px-1 mb-5">
                    <div className="w-full">
                        <p className="text-sm font-normal text-[#000] mb-2">Receive Readings Acknowledgement</p>
                        <ToggleSwitch
                            id="observer-reading-received-alert"
                            checked={getToggleValue('reading_received_alert')}
                            onChange={(value) => handleObserverToggle('reading_received_alert', value)}
                            disabled={updatingField === 'observer-reading_received_alert'}
                            dataTestId="observer-reading-received-alert-toggle"
                        />
                        {!hasValidMobileNumber && (
                            <p className="text-xs text-[#FF0000] mt-1">Mobile number required</p>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

